import {
  ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS,
  DeviceGroupPermissions,
  SoftwareSeatPermissions,
  WorkOrderPermissions,
} from '@order-management/roles';
import {
  DeferredResponse,
  Device,
  DeviceGroup,
  DeviceGroupData,
  DeviceGroupsCriteria,
  DeviceModel,
  InputType,
  LicensingProductGroup,
  PaginatedAPIResponse,
  SortDirection,
  WorkOrderDetailPageContext,
  WorkOrderStatus,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Box, Columns, Element, Icon, Section } from 'react-bulma-components';
import { FaArrowRight, FaCheck, FaMinus, FaPen, FaPlus } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { DeviceForSoftwareAssignmentStorageService } from '../../device-groups/device-for-software-assignment-storage-service';
import DeviceGroupConfirmDeletionModal from '../../device-groups/device-group-confirm-deletion-modal/device-group-confirm-deletion-modal';
import DeviceGroupInboxTable from '../../device-groups/device-group-inbox-table/device-group-inbox-table';
import { deleteDeviceGroupById } from '../../device-groups/device-groups-service';
import { SoftwareTitlesForAssignmentStorageService } from '../../device-groups/software-titles-for-assignment-storage-service';
import { fetchDeviceModels } from '../../devices/devices-service';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsEmpty from '../../shared/hooks/is-empty';
import usePermissions from '../../shared/hooks/permissions';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { fetchSoftwareTitles } from '../../software/software-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import Sticky from '../../ui/sticky/sticky';
import WorkOrderConfirmStatusUpdateModal from '../work-order-confirm-status-update-modal/work-order-confirm-status-update-modal';
import {
  fetchWorkOrderDeviceGroups as fetchWorkOrderDeviceGroupsRequest,
  sendWorkOrderToQueue,
} from '../work-orders-service';
import styles from './device-groups-dashboard-card-page.module.scss';

const devicesStorage = new DeviceForSoftwareAssignmentStorageService();
const softwareStorage = new SoftwareTitlesForAssignmentStorageService();

const FETCH_DEVICES_ERROR_MESSAGE =
  'Work order devices could not be fetched. Please try again';
const SEND_WORK_ORDER_TO_QUEUE_ERROR_MESSAGE =
  'The work order could not be sent to the queue. Please try again';
const MULTIPLE_DEVICE_TYPES_ERROR_MESSAGE =
  'You can only manage one device type at a time';
const NO_DEVICE_SELECTED_ERROR_MESSAGE =
  'You must select at least one device to manage software';

const initialCriteria: DeviceGroupsCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
  excludeEmptyGroups: true,
  includeUnmanageableByPermissions: true,
  includeHiddenGroups: true,
};

export const loader: LoaderFunction = async ({ params }) => {
  const { id } = params;

  const response = fetchWorkOrderDeviceGroupsRequest(
    id as string,
    initialCriteria
  );

  return defer({ response });
};

export function DeviceGroupsDashboardCardPageContent() {
  const navigate = useNavigate();
  const userHasPermissions = usePermissions();
  const { id } = useParams();
  const deviceGroupsInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<DeviceGroup>;
  const { workOrder, revalidate } =
    useOutletContext<WorkOrderDetailPageContext>();
  const [deviceGroupsDataResponse, setDeviceGroupsDataResponse] = useState<
    PaginatedAPIResponse<DeviceGroup>
  >(deviceGroupsInitialResponse);
  const { executeHttpRequest, isLoading: isSendingWorkOrderToQueue } =
    useHttpRequest();
  const { executeHttpRequest: executeFetchMoreWorkOrderDeviceGroups } =
    useHttpRequest();
  const {
    executeHttpRequest: executeFetchWorkOrderDeviceGroups,
    isLoading: isLoadingWorkOrderDeviceGroups,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeDeviceGroupDeletion,
    isLoading: isDeletingDeviceGroup,
  } = useHttpRequest();
  const [deviceGroupsFilterCriteria, setDeviceGroupsFilterCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const [baseDeviceGroupsCriteria, setBaseDeviceGroupsCriteria] =
    useState<DeviceGroupsCriteria>(initialCriteria);
  const isDataEmpty = useIsEmpty(deviceGroupsInitialResponse.data);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>(
    deviceGroupsInitialResponse.data
  );
  const [selectedDevicesByDeviceGroup, setSelectedDevicesByDeviceGroup] =
    useState<Map<string, Device[]>>(new Map<string, Device[]>());
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<DeviceGroup>();
  const [displayDeleteConfirmationModal, setDisplayDeleteConfirmationModal] =
    useState(false);
  const [manageSoftwareButtonPressed, setManageSoftwareButtonPressed] =
    useState(false);
  const [
    displaySendToQueueConfirmationModal,
    setDisplaySendToQueueConfirmationModal,
  ] = useState<boolean>(false);

  const selectedDevices = useMemo(
    () => Array.from(selectedDevicesByDeviceGroup.values()).flat(),
    [selectedDevicesByDeviceGroup]
  );

  const hasMoreDeviceGroups = useMemo(
    () => !!deviceGroupsDataResponse.hasMore,
    [deviceGroupsDataResponse]
  );

  const canBeSentToQueue = useMemo(() => {
    return workOrder.status === WorkOrderStatus.DRAFT;
  }, [workOrder.status]);

  const showNoDeviceSelectedError = useMemo(
    () => selectedDevices.length === 0,
    [selectedDevices]
  );

  const showMultipleDeviceModelsError = useMemo(() => {
    if (selectedDevices.length === 0) return false;

    const deviceModels = selectedDevices.map((device) => device.model.id);
    return deviceModels.some((deviceModel) => deviceModel !== deviceModels[0]);
  }, [selectedDevices]);

  const showErrorText = useMemo(
    () =>
      (showMultipleDeviceModelsError || showNoDeviceSelectedError) &&
      manageSoftwareButtonPressed,
    [
      showMultipleDeviceModelsError,
      showNoDeviceSelectedError,
      manageSoftwareButtonPressed,
    ]
  );

  const errorText = useMemo(() => {
    if (showMultipleDeviceModelsError) {
      return MULTIPLE_DEVICE_TYPES_ERROR_MESSAGE;
    } else if (showNoDeviceSelectedError) {
      return NO_DEVICE_SELECTED_ERROR_MESSAGE;
    }
  }, [showMultipleDeviceModelsError, showNoDeviceSelectedError]);

  const showFooter = useMemo(
    () =>
      userHasPermissions({
        permissions: WorkOrderPermissions.WORK_ORDERS_CREATE,
        allowedPermissionGroups: ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS,
      }) ||
      userHasPermissions({
        permissions: SoftwareSeatPermissions.SOFTWARE_SEATS_ASSIGN,
        allowedPermissionGroups: ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS,
      }),
    [userHasPermissions]
  );

  const disableDevicesSelection = useMemo(
    () =>
      !userHasPermissions({
        permissions: SoftwareSeatPermissions.SOFTWARE_SEATS_ASSIGN,
        allowedPermissionGroups: ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS,
      }),
    [userHasPermissions]
  );

  const tableData: DeviceGroupData[] = useMemo(
    () =>
      deviceGroups.map((dg) => ({
        id: dg.id,
        name: dg.name,
        groupCode: dg.groupCode,
        creator: dg.creator,
        devicesCount: dg.devices.length,
        visibleByUser: true,
      })),
    [deviceGroups]
  );

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: deviceGroupsFilterCriteria.salesOrders?.length,
        value: deviceGroupsFilterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      deviceTypes: {
        title: 'Device type',
        appliedCount: deviceGroupsFilterCriteria.deviceTypes?.length,
        value: deviceGroupsFilterCriteria.deviceTypes,
        input: {
          type: InputType.SELECT,
          valueKey: 'deviceTypes',
          fetchItems: fetchDeviceModels,
          resolveOption: (item: DeviceModel) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      softwareTitles: {
        title: 'Software',
        appliedCount: deviceGroupsFilterCriteria.softwareTitles?.length,
        value: deviceGroupsFilterCriteria.softwareTitles,
        input: {
          type: InputType.SELECT,
          valueKey: 'softwareTitles',
          fetchItems: fetchSoftwareTitles,
          resolveOption: (
            item: Pick<LicensingProductGroup, 'id' | 'displayName'>
          ) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      serialNumbers: {
        title: 'Serial number',
        appliedCount: deviceGroupsFilterCriteria.serialNumbers?.length,
        value: deviceGroupsFilterCriteria.serialNumbers,
        input: {
          type: InputType.FILTER_CRITERIA,
          valueKey: 'serialNumbers',
          filterValueKey: 'serialNumbers',
          placeholder: 'Search by Serial number',
        },
      },
    };
  }, [
    deviceGroupsFilterCriteria.deviceTypes,
    deviceGroupsFilterCriteria.salesOrders,
    deviceGroupsFilterCriteria.serialNumbers,
    deviceGroupsFilterCriteria.softwareTitles,
  ]) as FiltersData<DeviceGroupsCriteria>;

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const navigateToDeviceGroupCreation = useCallback(() => {
    navigate(`/work-orders/${id}/device-groups/create`);
  }, [id, navigate]);

  const handleOnManageSoftware = useCallback(() => {
    setManageSoftwareButtonPressed(true);
    if (!showNoDeviceSelectedError && !showMultipleDeviceModelsError) {
      devicesStorage.save(selectedDevices);
      softwareStorage.remove();

      const modelId = selectedDevices[0].model.id;
      navigate(
        `/work-orders/${id}/device-groups/device-models/${modelId}/software/select`
      );
    }
  }, [
    id,
    navigate,
    selectedDevices,
    showMultipleDeviceModelsError,
    showNoDeviceSelectedError,
  ]);

  const onDeleteDeviceGroup = useCallback(() => {
    setDisplayDeleteConfirmationModal(true);
  }, []);

  const handleDeviceGroupDeletion = useCallback(
    () =>
      executeDeviceGroupDeletion({
        asyncFunction: async () => {
          if (selectedDeviceGroup) {
            await deleteDeviceGroupById(selectedDeviceGroup.id);
            setDisplayDeleteConfirmationModal(false);
            revalidate();
          }
        },
      }),
    [executeDeviceGroupDeletion, revalidate, selectedDeviceGroup]
  );

  const onEditDeviceGroup = useCallback(() => {
    if (selectedDeviceGroup) {
      navigate(
        `/work-orders/${id}/device-groups/${selectedDeviceGroup.id}/edit`
      );
    }
  }, [id, navigate, selectedDeviceGroup]);

  const handleExitClick = useCallback(() => {
    navigate('/work-orders/dashboard/drafts');
  }, [navigate]);

  const handleCancelSendToQueue = useCallback(() => {
    setDisplaySendToQueueConfirmationModal(false);
  }, []);

  const handleSendToQueueClick = useCallback(
    async () =>
      executeHttpRequest({
        asyncFunction: async () => {
          await sendWorkOrderToQueue(id!);
          navigate('/work-orders/dashboard/queue');
        },
        customErrorMessage: SEND_WORK_ORDER_TO_QUEUE_ERROR_MESSAGE,
      }),
    [executeHttpRequest, id, navigate]
  );

  const resolveBaseDeviceGroupsCriteria = useCallback(
    (criteria: DeviceGroupsCriteria) => ({
      itemsPerPage: criteria.itemsPerPage,
      pageNumber: criteria.pageNumber,
      search: criteria.search,
      sortBy: criteria.sortBy,
      sortDirection: criteria.sortDirection,
      excludeEmptyGroups: criteria.excludeEmptyGroups,
      includeUnmanageableByPermissions:
        criteria.includeUnmanageableByPermissions,
      includeHiddenGroups: criteria.includeHiddenGroups,
    }),
    []
  );

  const hasBaseDeviceGroupsCriteriaChanged = useCallback(
    (newCriteria: DeviceGroupsCriteria) => {
      const keys = Object.keys(
        initialCriteria
      ) as (keyof DeviceGroupsCriteria)[];
      return keys.some(
        (key) => newCriteria[key] !== baseDeviceGroupsCriteria[key]
      );
    },
    [baseDeviceGroupsCriteria]
  );

  const fetchMoreDeviceGroups = useCallback(
    () =>
      executeFetchMoreWorkOrderDeviceGroups({
        asyncFunction: async () => {
          const nextPage = deviceGroupsFilterCriteria.pageNumber + 1;
          const updatedDeviceGroupCriteria = {
            ...deviceGroupsFilterCriteria,
            pageNumber: nextPage,
          };
          const workOrderDeviceGroupsResponse =
            await fetchWorkOrderDeviceGroupsRequest(
              id as string,
              resolveBaseDeviceGroupsCriteria(updatedDeviceGroupCriteria)
            );
          setDeviceGroupsFilterCriteria(updatedDeviceGroupCriteria);
          setDeviceGroups((prevDeviceGroups) =>
            prevDeviceGroups.concat(workOrderDeviceGroupsResponse.data)
          );
          setDeviceGroupsDataResponse(workOrderDeviceGroupsResponse);
        },
      }),
    [
      deviceGroupsFilterCriteria,
      executeFetchMoreWorkOrderDeviceGroups,
      id,
      resolveBaseDeviceGroupsCriteria,
    ]
  );

  const fetchWorkOrderDeviceGroups = useCallback(
    (deviceGroupsFilterCriteria: DeviceGroupsCriteria) =>
      executeFetchWorkOrderDeviceGroups({
        asyncFunction: async () => {
          const workOrderDeviceGroupsResponse =
            await fetchWorkOrderDeviceGroupsRequest(
              id as string,
              resolveBaseDeviceGroupsCriteria(deviceGroupsFilterCriteria)
            );
          setDeviceGroups(workOrderDeviceGroupsResponse.data);
        },
        customErrorMessage: FETCH_DEVICES_ERROR_MESSAGE,
      }),
    [executeFetchWorkOrderDeviceGroups, id, resolveBaseDeviceGroupsCriteria]
  );

  const handleManageDeviceClick = useCallback(
    (deviceGroupId: string, newDevices: Device[], add: boolean) => {
      const userHasDeviceGroupUpdatePermissions = userHasPermissions({
        permissions: DeviceGroupPermissions.DEVICE_GROUPS_UPDATE,
        allowedPermissionGroups: ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS,
      });

      if (userHasDeviceGroupUpdatePermissions) {
        setSelectedDevicesByDeviceGroup((devicesByDeviceGroup) => {
          const updatedDevicesByDeviceGroup = new Map(devicesByDeviceGroup);

          const deviceGroupDevices =
            updatedDevicesByDeviceGroup.get(deviceGroupId);
          if (add) {
            if (!deviceGroupDevices) {
              updatedDevicesByDeviceGroup.set(deviceGroupId, newDevices);
            } else {
              const updatedDevices = [...deviceGroupDevices, ...newDevices];
              updatedDevicesByDeviceGroup.set(
                deviceGroupId,
                updatedDevices.filter(
                  (device, index) =>
                    index ===
                    updatedDevices.findIndex((d) => d.id === device.id)
                )
              );
            }
          } else {
            if (deviceGroupDevices) {
              const newDevicesId = newDevices.map((device) => device.id);
              updatedDevicesByDeviceGroup.set(
                deviceGroupId,
                deviceGroupDevices.filter(
                  (device) => !newDevicesId.includes(device.id)
                )
              );
            }
          }
          return updatedDevicesByDeviceGroup;
        });
      }
    },
    [userHasPermissions]
  );

  const handleTableFilterSubmit = useCallback(
    async (value: DeviceGroupsCriteria) => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const updatedCriteria = {
        ...value,
        itemsPerPage,
        pageNumber,
      };
      setDeviceGroupsFilterCriteria(updatedCriteria);

      const updatedBaseDeviceGroupsCriteria =
        resolveBaseDeviceGroupsCriteria(updatedCriteria);
      const criteriaChanged = hasBaseDeviceGroupsCriteriaChanged(
        updatedBaseDeviceGroupsCriteria
      );

      if (criteriaChanged) {
        setBaseDeviceGroupsCriteria(updatedBaseDeviceGroupsCriteria);
        await fetchWorkOrderDeviceGroups(updatedCriteria);
      }
    },
    [
      fetchWorkOrderDeviceGroups,
      hasBaseDeviceGroupsCriteriaChanged,
      resolveBaseDeviceGroupsCriteria,
    ]
  );

  const isInvalidSelection = useCallback(
    (devices: Device[]) => {
      let isUniqueDeviceModelSelected = true;

      devices.forEach((device) => {
        isUniqueDeviceModelSelected =
          isUniqueDeviceModelSelected &&
          !selectedDevices.some(
            (selectedDevice) =>
              selectedDevice.id === device.id &&
              selectedDevices.some(
                (device) => device.model.id !== selectedDevice.model.id
              )
          );
      });

      return !isUniqueDeviceModelSelected;
    },
    [selectedDevices]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof DeviceGroupsCriteria)[]) => {
      const newData = { ...deviceGroupsFilterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [deviceGroupsFilterCriteria, handleTableFilterSubmit]
  );

  const handleCancelDeviceGroupDeletion = useCallback(() => {
    setDisplayDeleteConfirmationModal(false);
  }, []);

  const handleSelectDeviceGroup = useCallback(
    (deviceGroupId: string) => {
      const deviceGroup = deviceGroups.find((dg) => (dg.id = deviceGroupId));
      setSelectedDeviceGroup(deviceGroup);
    },
    [deviceGroups]
  );

  const Header = useCallback(
    () => (
      <Element className="p-4">
        <Columns marginless>
          <Columns.Column>
            <h1 className="is-size-3 has-text-weight-light">Device groups</h1>
          </Columns.Column>
          <Columns.Column
            display="flex"
            justifyContent="flex-end"
            className="gap-3"
          >
            <CheckPermissions
              permissions={DeviceGroupPermissions.DEVICE_GROUPS_CREATE}
              allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
            >
              <CheckPermissions.Render>
                <Button
                  color="primary-dark"
                  onClick={navigateToDeviceGroupCreation}
                >
                  <Icon>
                    <FaPlus />
                  </Icon>
                  <span>Create device group</span>
                </Button>
              </CheckPermissions.Render>
            </CheckPermissions>
            <CheckPermissions
              permissions={SoftwareSeatPermissions.SOFTWARE_SEATS_ASSIGN}
              allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
            >
              <CheckPermissions.Render>
                <Button
                  className="outlined-button-white-background"
                  color="primary-dark"
                  outlined
                  onClick={handleOnManageSoftware}
                >
                  <span>Manage software</span>
                  <Icon>
                    <FaArrowRight />
                  </Icon>
                </Button>
              </CheckPermissions.Render>
            </CheckPermissions>
          </Columns.Column>
        </Columns>
      </Element>
    ),
    [handleOnManageSoftware, navigateToDeviceGroupCreation]
  );

  const EmptyState = useCallback(
    () => (
      <Section
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingless
      >
        <span className="mb-3 has-text-weight-light is-size-5">
          No device groups yet
        </span>
        <CheckPermissions
          permissions={DeviceGroupPermissions.DEVICE_GROUPS_CREATE}
          allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
        >
          <CheckPermissions.Render>
            <span className="is-size-6 mb-5">
              You need to group your devices in order to move forward
            </span>
            <Button
              color="primary-dark"
              onClick={navigateToDeviceGroupCreation}
            >
              <Icon>
                <FaPlus />
              </Icon>
              <span>Create device group</span>
            </Button>
          </CheckPermissions.Render>
        </CheckPermissions>
      </Section>
    ),
    [navigateToDeviceGroupCreation]
  );

  const Footer = useCallback(
    () => (
      <Box
        display="flex"
        justifyContent="center"
        py={4}
        className={styles.footer}
      >
        <CheckPermissions
          permissions={SoftwareSeatPermissions.SOFTWARE_SEATS_ASSIGN}
          allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
        >
          <CheckPermissions.Render>
            <Button color="primary-dark" outlined onClick={handleExitClick}>
              Save & exit
            </Button>
          </CheckPermissions.Render>
        </CheckPermissions>
        <CheckPermissions
          permissions={WorkOrderPermissions.WORK_ORDERS_CREATE}
          allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
        >
          <CheckPermissions.Render>
            <If condition={canBeSentToQueue}>
              <Button
                color="primary-dark"
                onClick={() => setDisplaySendToQueueConfirmationModal(true)}
              >
                <Button.LoadingIcon icon={FaCheck} />
                <span>Send to queue</span>
              </Button>
            </If>
          </CheckPermissions.Render>
        </CheckPermissions>
      </Box>
    ),
    [canBeSentToQueue, handleExitClick]
  );

  const DeviceGroupActions = useCallback(
    () => (
      <Element
        display="flex"
        className="w-full"
        alignItems="center"
        justifyContent="space-between"
      >
        <FilterBar
          initialData={initialCriteria}
          data={deviceGroupsFilterCriteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />

        <Element alignItems="flex-end">
          <If condition={!!selectedDeviceGroup}>
            <CheckPermissions
              permissions={DeviceGroupPermissions.DEVICE_GROUPS_UPDATE}
              allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
            >
              <CheckPermissions.Render>
                <Button
                  color="transparent"
                  className={styles.actionButton}
                  onClick={onEditDeviceGroup}
                >
                  <Icon color="primary-dark">
                    <FaPen />
                  </Icon>
                  <span className="is-underlined has-text-primary-dark">
                    Edit device group
                  </span>
                </Button>
              </CheckPermissions.Render>
            </CheckPermissions>
            <CheckPermissions
              permissions={DeviceGroupPermissions.DEVICE_GROUPS_DELETE}
              allowedPermissionGroups={ALL_ZSPACE_STAFF_PERMISSIONS_GROUPS}
            >
              <CheckPermissions.Render>
                <Button
                  color="transparent"
                  className={styles.actionButton}
                  onClick={onDeleteDeviceGroup}
                >
                  <Icon color="primary-dark">
                    <FaMinus />
                  </Icon>
                  <span className="is-underlined has-text-primary-dark">
                    Delete device group
                  </span>
                </Button>
              </CheckPermissions.Render>
            </CheckPermissions>
          </If>
        </Element>
      </Element>
    ),
    [
      deviceGroupsFilterCriteria,
      filtersData,
      handleTableFilterSubmit,
      onDeleteDeviceGroup,
      onEditDeviceGroup,
      onRemoveFilters,
      selectedDeviceGroup,
    ]
  );

  return (
    <>
      <BoxLayout className="is-min-height-80 mx-10 my-4" header={<Header />}>
        <Conditional condition={isDataEmpty}>
          <Conditional.True>
            <EmptyState />
          </Conditional.True>
          <Conditional.False>
            <If condition={showErrorText}>
              <Element mb={2}>
                <FeedbackMessage>{errorText}</FeedbackMessage>
              </Element>
            </If>
            <DeviceGroupActions />
            <DeviceGroupInboxTable
              deviceGroups={tableData}
              fetchMoreDeviceGroups={fetchMoreDeviceGroups}
              hasMoreDeviceGroups={hasMoreDeviceGroups}
              onManageDeviceClick={handleManageDeviceClick}
              selectedDevicesByDeviceGroup={selectedDevicesByDeviceGroup}
              loadingInitialData={isLoadingWorkOrderDeviceGroups}
              deviceGroupsCriteriaFilter={deviceGroupsFilterCriteria}
              isInvalidSelection={isInvalidSelection}
              onDeviceGroupClick={(dg) => handleSelectDeviceGroup(dg.id)}
              workOrderId={id}
              disableSelection={disableDevicesSelection}
              disableDeviceGroupActions={true}
              appliedFilters={appliedFilters}
            />
          </Conditional.False>
        </Conditional>
      </BoxLayout>

      <If condition={!isDataEmpty && showFooter}>
        <Sticky>
          <Footer />
        </Sticky>
      </If>

      <DeviceGroupConfirmDeletionModal
        show={displayDeleteConfirmationModal}
        deviceGroupName={selectedDeviceGroup?.name ?? ''}
        loading={isDeletingDeviceGroup}
        onCancel={handleCancelDeviceGroupDeletion}
        onDelete={handleDeviceGroupDeletion}
      />

      <WorkOrderConfirmStatusUpdateModal
        show={displaySendToQueueConfirmationModal}
        targetStatus={WorkOrderStatus.QUEUE}
        onCancel={handleCancelSendToQueue}
        onConfirm={handleSendToQueueClick}
        loading={isSendingWorkOrderToQueue}
      />
    </>
  );
}

export function DeviceGroupsDashboardCardPage() {
  const { response } = useLoaderData() as DeferredResponse<
    [PaginatedAPIResponse<DeviceGroup>, DeviceModel[]]
  >;

  return (
    <ProtectedPage
      permissions={[
        DeviceGroupPermissions.DEVICE_GROUPS_READ,
        SoftwareSeatPermissions.SOFTWARE_SEATS_READ,
      ]}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <DeviceGroupsDashboardCardPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default DeviceGroupsDashboardCardPage;
